import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Bot, Plus, User as UserIcon } from 'lucide-react';
import { createUser, deactivateUser, reactivateUser } from '@mkcore/platform/schema';
import {
  Badge,
  BulkBar,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Label,
  NativeSelect,
  PageHeader,
  SelectAllCheckbox,
  SelectRowCheckbox,
  Skeleton,
  TBody,
  THead,
  Table,
  Td,
  Th,
  Tr,
  toast,
  useSelection,
} from '@mkcore/ui';
import { ApiError, call, newIdempotencyKey } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { rolesQuery, usersQuery } from '@/features/admin/queries';

// Filters, sorting and the open dialog live in the URL, so a shared link shows the same view.
const search = z.object({
  q: z.string().optional(),
  kind: z.enum(['human', 'agent']).optional(),
  status: z.enum(['active', 'deactivated']).optional(),
  deleted: z.boolean().optional(),
  new: z.boolean().optional(),
});

export const Route = createFileRoute('/_app/admin/users/')({ validateSearch: search, component: Users });

function Users() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const qc = useQueryClient();
  const canCreate = useCan('admin:create');
  const canDelete = useCan('admin:delete');
  const canUpdate = useCan('admin:update');
  const errorMessage = useErrorMessage();
  const query = useInfiniteQuery(
    usersQuery({ q: s.q || undefined, kind: s.kind, status: s.status, includeDeleted: s.deleted || undefined }),
  );
  const users = useMemo(() => query.data?.pages.flatMap((p) => p.items) ?? [], [query.data]);
  const ids = useMemo(() => users.map((u) => u.id), [users]);
  const selection = useSelection(ids);
  const [bulkBusy, setBulkBusy] = useState(false);

  const setSearch = (patch: Partial<z.infer<typeof search>>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  const bulk = async (kind: 'deactivate' | 'reactivate') => {
    setBulkBusy(true);
    let done = 0;
    for (const id of selection.selected) {
      try {
        await call(kind === 'deactivate' ? deactivateUser : reactivateUser, { id }, { idempotencyKey: newIdempotencyKey() });
        done++;
      } catch (e) {
        toast.error(`${users.find((u) => u.id === id)?.name}: ${errorMessage(e)}`);
      }
    }
    setBulkBusy(false);
    selection.clear();
    if (done) toast.success(t('users.bulkDone', { count: done }));
    void qc.invalidateQueries({ queryKey: ['admin', 'user'] });
  };

  return (
    <>
      <PageHeader
        eyebrow={t('nav.admin')}
        title={t('users.title')}
        description={t('users.description')}
        actions={
          canCreate && (
            <Button onClick={() => setSearch({ new: true })}>
              <Plus /> {t('users.new')}
            </Button>
          )
        }
      />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('common.search')} className="w-full sm:w-64">
          {(p) => <Input {...p} type="search" defaultValue={s.q} onChange={(e) => setSearch({ q: e.target.value || undefined })} />}
        </Field>
        <Field label={t('users.kind')} className="w-40">
          {(p) => (
            <NativeSelect {...p} value={s.kind ?? ''} onChange={(e) => setSearch({ kind: (e.target.value || undefined) as never })}>
              <option value="">{t('common.all')}</option>
              <option value="human">{t('users.kindHuman')}</option>
              <option value="agent">{t('users.kindAgent')}</option>
            </NativeSelect>
          )}
        </Field>
        <Field label={t('common.status')} className="w-44">
          {(p) => (
            <NativeSelect {...p} value={s.status ?? ''} onChange={(e) => setSearch({ status: (e.target.value || undefined) as never })}>
              <option value="">{t('common.all')}</option>
              <option value="active">{t('users.statusActive')}</option>
              <option value="deactivated">{t('users.statusDeactivated')}</option>
            </NativeSelect>
          )}
        </Field>
        <label className="flex h-10 items-center gap-2 text-sm">
          <Checkbox checked={!!s.deleted} onCheckedChange={(v) => setSearch({ deleted: v === true || undefined })} />
          {t('users.includeDeleted')}
        </label>
      </div>

      {query.isError ? (
        <ErrorState title={t('errors.generic')} description={errorMessage(query.error)} />
      ) : query.isLoading ? (
        <Skeleton className="h-64" />
      ) : users.length === 0 ? (
        <EmptyState title={t('users.empty')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th className="w-8">
                <SelectAllCheckbox selection={selection} label={t('common.selectAll')} />
              </Th>
              <Th>{t('common.name')}</Th>
              <Th className="hidden sm:table-cell">{t('users.roles')}</Th>
              <Th>{t('common.status')}</Th>
              <Th className="hidden md:table-cell">{t('users.lastLogin')}</Th>
            </Tr>
          </THead>
          <TBody>
            {users.map((u) => (
              <Tr key={u.id} data-selected={selection.isSelected(u.id)}>
                <Td>
                  <SelectRowCheckbox selection={selection} rowKey={u.id} label={t('common.selectRow', { name: u.name })} />
                </Td>
                <Td>
                  <Link to="/admin/users/$id" params={{ id: u.id }} className="flex items-center gap-2 font-bold hover:underline [&_svg]:size-4 [&_svg]:text-fg-3">
                    {u.kind === 'agent' ? <Bot aria-label={t('users.kindAgent')} /> : <UserIcon aria-label={t('users.kindHuman')} />}
                    {u.name}
                  </Link>
                  <div className="text-xs text-fg-3">{u.email ?? (u.kind === 'agent' ? t('users.kindAgent') : '')}</div>
                </Td>
                <Td className="hidden sm:table-cell">
                  <div className="flex flex-wrap gap-1">
                    {u.roleKeys.map((r) => (
                      <Badge key={r} tone="outline">
                        {r}
                      </Badge>
                    ))}
                  </div>
                </Td>
                <Td>
                  {u.deletedAt ? (
                    <Badge tone="danger">{t('users.deleted')}</Badge>
                  ) : u.status === 'active' ? (
                    <Badge tone="success">{t('users.statusActive')}</Badge>
                  ) : (
                    <Badge tone="neutral">{t('users.statusDeactivated')}</Badge>
                  )}
                </Td>
                <Td className="hidden md:table-cell tabular text-fg-2">{u.lastLoginAt ? formatDateTime(u.lastLoginAt) : t('users.never')}</Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      {query.hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button variant="secondary" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}

      <BulkBar
        count={selection.count}
        label={t('common.selected', { count: selection.count })}
        clearLabel={t('common.clearSelection')}
        onClear={selection.clear}
      >
        {canDelete && (
          <Button size="sm" variant="secondary" loading={bulkBusy} onClick={() => void bulk('deactivate')}>
            {t('users.bulkDeactivate')}
          </Button>
        )}
        {canUpdate && (
          <Button size="sm" variant="secondary" loading={bulkBusy} onClick={() => void bulk('reactivate')}>
            {t('users.bulkReactivate')}
          </Button>
        )}
      </BulkBar>

      <CreateUserDialog open={!!s.new} onOpenChange={(o) => setSearch({ new: o || undefined })} />
    </>
  );
}

function CreateUserDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const roles = useQuery(rolesQuery);
  const [kind, setKind] = useState<'human' | 'agent'>('human');
  const [form, setForm] = useState({ name: '', email: '', entraTenantId: '', entraObjectId: '' });
  const [roleKeys, setRoleKeys] = useState<string[]>(['employee']);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [key] = useState(newIdempotencyKey);
  const create = useActionMutation(createUser, {
    invalidate: [['admin', 'user']],
    success: t('users.created'),
    silentErrors: true,
    onSuccess: (u) => {
      onOpenChange(false);
      void navigate({ to: '/admin/users/$id', params: { id: u.id } });
    },
  });
  const message = useErrorMessage();
  const field = (k: keyof typeof form) => ({
    value: form[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{kind === 'agent' ? t('users.newAgent') : t('users.new')}</DialogTitle>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            setErrors({});
            create.mutate(
              {
                key,
                input: {
                  kind,
                  name: form.name,
                  roleKeys,
                  ...(kind === 'human' && {
                    email: form.email || null,
                    entraTenantId: form.entraTenantId || null,
                    entraObjectId: form.entraObjectId || null,
                  }),
                },
              },
              {
                onError: (err) => {
                  if (err instanceof ApiError) setErrors({ ...err.fieldErrors(), _: message(err) });
                  else setErrors({ _: message(err) });
                },
              },
            );
          }}
        >
          <div role="radiogroup" aria-label={t('users.kind')} className="flex gap-2">
            {(['human', 'agent'] as const).map((k) => (
              <Button key={k} type="button" size="sm" variant={kind === k ? 'primary' : 'secondary'} aria-pressed={kind === k} onClick={() => {
                setKind(k);
                setRoleKeys([k === 'agent' ? 'agent' : 'employee']);
              }}>
                {k === 'human' ? t('users.kindHuman') : t('users.kindAgent')}
              </Button>
            ))}
          </div>
          <Field label={t('common.name')} error={errors.name}>
            {(p) => <Input {...p} required maxLength={200} {...field('name')} />}
          </Field>
          {kind === 'human' && (
            <>
              <Field label={t('common.email')} error={errors.email}>
                {(p) => <Input {...p} type="email" {...field('email')} />}
              </Field>
              <Field label={t('users.entraTenantId')} hint={t('users.entraHint')} error={errors.entraTenantId}>
                {(p) => <Input {...p} className="font-mono text-xs" {...field('entraTenantId')} />}
              </Field>
              <Field label={t('users.entraObjectId')} error={errors.entraObjectId}>
                {(p) => <Input {...p} className="font-mono text-xs" {...field('entraObjectId')} />}
              </Field>
            </>
          )}
          <fieldset className="grid gap-2">
            <Label asChild>
              <legend>{t('users.roles')}</legend>
            </Label>
            <div className="flex flex-wrap gap-3">
              {roles.data?.items.map((r) => (
                <label key={r.id} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={roleKeys.includes(r.key)}
                    onCheckedChange={(v) => setRoleKeys(v === true ? [...roleKeys, r.key] : roleKeys.filter((x) => x !== r.key))}
                  />
                  {r.name}
                </label>
              ))}
            </div>
          </fieldset>
          {errors._ && (
            <p role="alert" className="text-sm text-danger">
              {errors._}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={create.isPending}>
              {t('common.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
